/**
 * BulkAssignPlanModal
 *
 * Modal for assigning a subscription plan to multiple organizations at once.
 * Used by superadmins from the Subscriptions tab in the Subscription Management page.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  BuildingOfficeIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import { useSubscriptionStore, SubscriptionPlan } from '@/stores/subscriptionStore';
import toast from 'react-hot-toast';
import { formatPrice, formatLimit, getEffectivePrice } from '@/utils/subscriptionFormat';
import { TIER_BADGE_BORDER_CLASSES, tierClasses } from '../../utils/planTiers';

interface OrganizationInfo {
  id: string;
  name: string;
  currentPlanId?: string;
  currentPlanName?: string;
}

interface BulkAssignPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizations: OrganizationInfo[];
  onSuccess?: () => void;
}

export function BulkAssignPlanModal({
  isOpen,
  onClose,
  organizations,
  onSuccess
}: BulkAssignPlanModalProps) {
  const { plans, plansLoading, fetchPlans, bulkAssignPlan } = useSubscriptionStore();
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && plans.length === 0) {
      fetchPlans();
    }
  }, [isOpen, plans.length, fetchPlans]);

  const activePlans = plans.filter(p => p.isActive);
  const selectedPlan = activePlans.find(p => p.planId === selectedPlanId);

  const handleSubmit = async () => {
    if (!selectedPlanId || organizations.length === 0) return;

    setSubmitting(true);
    try {
      const orgIds = organizations.map(o => o.id);
      const result = await bulkAssignPlan(orgIds, selectedPlanId);

      if (result.failed.length === 0) {
        toast.success(`Plan assigned to ${result.success.length} organization(s)`);
      } else if (result.success.length > 0) {
        toast.success(`Plan assigned to ${result.success.length} organization(s)`);
        toast.error(`Failed for ${result.failed.length} organization(s)`);
      } else {
        toast.error('Failed to assign plan to any organization');
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to assign plan:', error);
      toast.error(error.response?.data?.error || 'Failed to assign plan');
    } finally {
      setSubmitting(false);
    }
  };

  // formatPrice, formatLimit, getEffectivePrice imported from shared utils

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CreditCardIcon className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Assign Plan</h2>
                <p className="text-sm text-gray-600">
                  {organizations.length} organization{organizations.length !== 1 ? 's' : ''} selected
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            {/* Selected Organizations */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Selected Organizations</h3>
              <div className="bg-gray-50 rounded-lg p-4 max-h-32 overflow-y-auto">
                <div className="flex flex-wrap gap-2">
                  {organizations.map((org) => (
                    <span
                      key={org.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm"
                    >
                      <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-700">{org.name}</span>
                      {org.currentPlanName && (
                        <span className="text-xs text-gray-400">({org.currentPlanName})</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Plan Selection */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Select Plan to Assign</h3>

              {plansLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : activePlans.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <ExclamationTriangleIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No active plans available.</p>
                  <p className="text-sm text-gray-400 mt-1">Create a plan first in the Plans tab.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activePlans.map((plan) => {
                    const isSelected = selectedPlanId === plan.planId;

                    return (
                      <button
                        key={plan.planId}
                        onClick={() => setSelectedPlanId(plan.planId)}
                        className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${tierClasses(TIER_BADGE_BORDER_CLASSES, plan.tier, 'bg-gray-100 text-gray-700 border-gray-200')}`}>
                            {plan.tier}
                          </span>
                          {isSelected && (
                            <CheckIcon className="h-5 w-5 text-purple-600" />
                          )}
                        </div>
                        <h4 className="font-semibold text-gray-900">{plan.name}</h4>
                        <p className="text-lg font-bold text-gray-900 mt-1">
                          {formatPrice(getEffectivePrice(plan), plan.pricing.billingCycle)}
                        </p>
                        <div className="text-xs text-gray-500 mt-2 space-y-0.5">
                          <p>{formatLimit(plan.included.encountersPerMonth)} encounters/mo</p>
                          <p>{formatLimit(plan.included.transcriptionMinutes)} minutes</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Summary */}
            {selectedPlan && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Assignment Summary</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>Plan: <span className="font-medium">{selectedPlan.name}</span></li>
                  <li>Organizations: <span className="font-medium">{organizations.length}</span></li>
                  <li>Price: <span className="font-medium">{formatPrice(selectedPlan.pricing.basePrice, selectedPlan.pricing.billingCycle)}</span> each</li>
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedPlanId || submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Assigning...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Assign Plan
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default BulkAssignPlanModal;
